import { useLanderConfig } from "../commons/ConfigContext";
import { isRollerAdsGeoOverride } from "../commons/HelperFunctions";
import { FeedType, LanderConfig } from "../types/LanderConfig";
import { buildRsocRedirectUrl } from "../rsoc/useRsoc";

export enum AdProvider {
  RSOC = "rsoc",
  ROLLER_ADS = "roller_ads",
  YAHOO = "yahoo",
}

export interface AdProviderResult {
  provider: AdProvider;
  rsocUrl: string | null;
  rsocError: boolean;
}

function isRsocEligible(landerConfig: LanderConfig): boolean {
  const rsocPathOrUrlExists =
    !!landerConfig.rsoc?.path || !!landerConfig.rsoc?.url;
  const feedIDIsRsoc = landerConfig.lander?.feedID === FeedType.RSOC;
  const domainIsCloudynestStore =
    landerConfig.domain?.rootDomain === "cloudynest.store";

  return rsocPathOrUrlExists && feedIDIsRsoc && !domainIsCloudynestStore;
}

/**
 * Resolves which ad provider to use. Priority order:
 * 1. Geo-override (CN + the keyword-click geos, see isRollerAdsGeoOverride)
 *    -> ROLLER_ADS
 * 2. RSOC-eligible (feedID=1, path/url exists, not cloudynest) -> RSOC
 * 3. feedID === ROLLER_ADS -> ROLLER_ADS
 * 4. Otherwise -> YAHOO
 */
export function useAdProvider(): AdProviderResult {
  const { landerConfig } = useLanderConfig();

  let provider: AdProvider;
  let rsocUrl: string | null = null;
  let rsocError = false;

  if (isRollerAdsGeoOverride()) {
    provider = AdProvider.ROLLER_ADS;
  } else if (isRsocEligible(landerConfig)) {
    const result = buildRsocRedirectUrl(landerConfig);
    if (result.url) {
      provider = AdProvider.RSOC;
      rsocUrl = result.url;
    } else {
      rsocError = true;
      provider = AdProvider.YAHOO;
    }
  } else if (landerConfig.lander?.feedID === FeedType.ROLLER_ADS) {
    provider = AdProvider.ROLLER_ADS;
  } else {
    provider = AdProvider.YAHOO;
  }

  return { provider, rsocUrl, rsocError };
}
